import { useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Info } from 'lucide-react';
import { Card } from '@/components/ui/card';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface StatsCardProps {
  label: string;
  value: string | number;
  icon: LucideIcon;
  colorClass: string;
  bgColorClass: string;
  borderColorClass: string;
  onClick?: () => void;
  isActive?: boolean;
  isMinimized?: boolean;
  infoTooltip?: string;
}

export function StatsCard({
  label,
  value,
  icon: Icon,
  colorClass,
  bgColorClass,
  borderColorClass,
  onClick,
  isActive = false,
  isMinimized = false,
  infoTooltip,
}: StatsCardProps) {
  const isClickable = !!onClick;
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [clickedOpen, setClickedOpen] = useState(false);

  const handleInfoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (clickedOpen) {
      setIsTooltipOpen(false);
      setClickedOpen(false);
    } else {
      setIsTooltipOpen(true);
      setClickedOpen(true);
    }
  };

  const handleTooltipOpenChange = (open: boolean) => {
    if (open && !clickedOpen) {
      setIsTooltipOpen(true);
    } else if (!open && !clickedOpen) {
      setIsTooltipOpen(false);
    }
  };

  const handleOutsideClick = () => {
    setIsTooltipOpen(false);
    setClickedOpen(false);
  };

  return (
    <Card
      className={cn(
        'relative overflow-hidden transition-all duration-300 border-0 border-l-4',
        borderColorClass,
        isClickable && 'cursor-pointer hover:shadow-lg hover:-translate-y-1',
        isActive && 'ring-2 ring-offset-2',
        isActive && colorClass.replace('text-', 'ring-')
      )}
      onClick={onClick}
    >
      <div className={cn(
        'transition-all duration-300',
        isMinimized ? 'p-3 sm:p-3.5' : 'p-4 sm:p-5'
      )}>
        {/* Header with icon */}
        <div className={cn(
          'flex items-start justify-between',
          isMinimized ? 'mb-2' : 'mb-3'
        )}>
          <div className={cn(
            'rounded-xl transition-all duration-300',
            isMinimized ? 'p-1.5' : 'p-2.5',
            bgColorClass
          )}>
            <Icon className={cn(
              'transition-all duration-300',
              isMinimized ? 'h-4 w-4' : 'h-5 w-5',
              colorClass
            )} />
          </div>
          {infoTooltip && (
            <TooltipProvider>
              <Tooltip
                delayDuration={300}
                open={isTooltipOpen}
                onOpenChange={handleTooltipOpenChange}
              >
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    aria-label="More information"
                    onClick={handleInfoClick}
                  >
                    <Info className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
                  </button>
                </TooltipTrigger>
                <TooltipContent
                  side="top"
                  className="max-w-xs text-sm"
                  onPointerDownOutside={handleOutsideClick}
                >
                  <p>{infoTooltip}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>

        {/* Value and label */}
        <div className="space-y-1 transition-all duration-300">
          <div className={cn(
            'font-bold text-gray-900 dark:text-gray-50 transition-all duration-300',
            isMinimized ? 'text-2xl sm:text-3xl' : 'text-3xl sm:text-4xl'
          )}>
            {value}
          </div>
          <div className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
            {label}
          </div>
        </div>

        {/* Gradient overlay for depth */}
        <div
          className={cn(
            'absolute inset-0 opacity-5 pointer-events-none',
            bgColorClass
          )}
        />
      </div>
    </Card>
  );
}
